/**
 * Customer-cloud media classification.
 *
 * Camera/video originals may remain in the source folder or browser-local
 * stores. A cloud upload is eligible only when the caller carries an explicit
 * derivative receipt produced by a known transform. This client-side seam is
 * intentionally fail-closed; the backend applies its own trusted gate.
 */

export const CLOUD_MEDIA_CLASSES = Object.freeze({
    LOCAL_ORIGINAL: 'local_original',
    COMPRESSED_DERIVATIVE: 'compressed_derivative',
} as const);

export const CLOUD_MEDIA_KINDS = Object.freeze({
    PHOTO_PREVIEW: 'photo_preview',
    VIDEO_PROXY: 'video_proxy',
    VIDEO_FRAME: 'video_frame',
} as const);

export const PHOTO_DERIVATIVE_PROFILE_V1 = 'qm-photo-preview-1280-v1';
export const VIDEO_FRAME_DERIVATIVE_PROFILE_V1 = 'qm-video-frame-1280-v1';
export const VIDEO_PROXY_DERIVATIVE_PROFILE_V1 = 'qm-video-proxy-720p-h264-v1';

export type CloudMediaClass = (typeof CLOUD_MEDIA_CLASSES)[keyof typeof CLOUD_MEDIA_CLASSES];
export type CloudMediaKind = (typeof CLOUD_MEDIA_KINDS)[keyof typeof CLOUD_MEDIA_KINDS];

export interface CloudMediaClassificationV1 {
    schemaVersion: 1;
    mediaClass: CloudMediaClass;
    derivativeKind?: CloudMediaKind;
    transformProfile?: string;
    transformVersion?: number;
    processor?: string;
    derivativeBytes?: number;
    derivativeContentType?: string;
    width?: number;
    height?: number;
    localOnlyReason?: string;
}

export interface CompressedPhotoCloudMediaInput {
    derivativeBytes: number;
    contentType: string;
    width: number;
    height: number;
    processor: string;
}

export interface CompressedVideoFrameCloudMediaInput {
    derivativeBytes: number;
    contentType: string;
    processor: string;
    width?: number;
    height?: number;
}

export interface CompressedVideoProxyCloudMediaInput {
    derivativeBytes: number;
    contentType: string;
    processor: string;
}

export interface CloudMediaUploadDecision {
    allowed: boolean;
    reason:
        | 'verified_compressed_derivative'
        | 'raw_original_cloud_upload_forbidden'
        | 'missing_derivative_attestation'
        | 'incomplete_derivative_attestation'
        | 'derivative_kind_mismatch';
}

function positiveFinite(value: unknown, name: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        throw new TypeError(`${name} must be a positive finite number`);
    }
    return value;
}

function nonEmpty(value: unknown, name: string): string {
    if (typeof value !== 'string' || value.trim().length === 0) {
        throw new TypeError(`${name} must be a non-empty string`);
    }
    return value.trim();
}

export function buildCompressedPhotoCloudMedia(
    input: CompressedPhotoCloudMediaInput,
): CloudMediaClassificationV1 {
    const contentType = nonEmpty(input.contentType, 'contentType').toLowerCase();
    if (contentType !== 'image/jpeg' && contentType !== 'image/webp') {
        throw new TypeError('contentType must be image/jpeg or image/webp');
    }
    return {
        schemaVersion: 1,
        mediaClass: CLOUD_MEDIA_CLASSES.COMPRESSED_DERIVATIVE,
        derivativeKind: CLOUD_MEDIA_KINDS.PHOTO_PREVIEW,
        transformProfile: PHOTO_DERIVATIVE_PROFILE_V1,
        transformVersion: 1,
        processor: nonEmpty(input.processor, 'processor'),
        derivativeBytes: Math.round(positiveFinite(input.derivativeBytes, 'derivativeBytes')),
        derivativeContentType: contentType,
        width: Math.round(positiveFinite(input.width, 'width')),
        height: Math.round(positiveFinite(input.height, 'height')),
    };
}

export function buildLocalOriginalCloudMedia(reason: string): CloudMediaClassificationV1 {
    return {
        schemaVersion: 1,
        mediaClass: CLOUD_MEDIA_CLASSES.LOCAL_ORIGINAL,
        localOnlyReason: nonEmpty(reason, 'reason'),
    };
}

export function buildCompressedVideoFrameCloudMedia(
    input: CompressedVideoFrameCloudMediaInput,
): CloudMediaClassificationV1 {
    const contentType = nonEmpty(input.contentType, 'contentType').toLowerCase();
    if (contentType !== 'image/jpeg' && contentType !== 'image/webp') {
        throw new TypeError('contentType must be image/jpeg or image/webp');
    }
    return {
        schemaVersion: 1,
        mediaClass: CLOUD_MEDIA_CLASSES.COMPRESSED_DERIVATIVE,
        derivativeKind: CLOUD_MEDIA_KINDS.VIDEO_FRAME,
        transformProfile: VIDEO_FRAME_DERIVATIVE_PROFILE_V1,
        transformVersion: 1,
        processor: nonEmpty(input.processor, 'processor'),
        derivativeBytes: Math.round(positiveFinite(input.derivativeBytes, 'derivativeBytes')),
        derivativeContentType: contentType,
        ...(input.width == null ? {} : { width: Math.round(positiveFinite(input.width, 'width')) }),
        ...(input.height == null ? {} : { height: Math.round(positiveFinite(input.height, 'height')) }),
    };
}

export function buildCompressedVideoProxyCloudMedia(
    input: CompressedVideoProxyCloudMediaInput,
): CloudMediaClassificationV1 {
    const contentType = nonEmpty(input.contentType, 'contentType').toLowerCase();
    if (contentType !== 'video/mp4') {
        throw new TypeError('contentType must be video/mp4');
    }
    return {
        schemaVersion: 1,
        mediaClass: CLOUD_MEDIA_CLASSES.COMPRESSED_DERIVATIVE,
        derivativeKind: CLOUD_MEDIA_KINDS.VIDEO_PROXY,
        transformProfile: VIDEO_PROXY_DERIVATIVE_PROFILE_V1,
        transformVersion: 1,
        processor: nonEmpty(input.processor, 'processor'),
        derivativeBytes: Math.round(positiveFinite(input.derivativeBytes, 'derivativeBytes')),
        derivativeContentType: contentType,
    };
}

const ENTITY_DERIVATIVE_KIND = Object.freeze({
    project_photo: CLOUD_MEDIA_KINDS.PHOTO_PREVIEW,
    project_video_frame: CLOUD_MEDIA_KINDS.VIDEO_FRAME,
    project_video_proxy: CLOUD_MEDIA_KINDS.VIDEO_PROXY,
    intake_visit_photo_preview: CLOUD_MEDIA_KINDS.PHOTO_PREVIEW,
    intake_visit_video_proxy: CLOUD_MEDIA_KINDS.VIDEO_PROXY,
} as const);
const SYSTEM_MEDIA_ENTITY_TYPES = new Set(['gold_standard_thumbnail']);

export function expectedCloudMediaKindForEntityType(entityType: unknown): CloudMediaKind | null {
    if (typeof entityType !== 'string') return null;
    return ENTITY_DERIVATIVE_KIND[entityType as keyof typeof ENTITY_DERIVATIVE_KIND] || null;
}

export function isForbiddenRawCustomerMediaUpload(entityType: unknown, contentType: unknown): boolean {
    const mime = typeof contentType === 'string' ? contentType.trim().toLowerCase() : '';
    if (entityType === 'project_video') return mime.startsWith('video/');
    return entityType === 'intake_visit_media'
        && (mime.startsWith('image/') || mime.startsWith('video/'));
}

export function isUnclassifiedCustomerMediaUpload(entityType: unknown, contentType: unknown): boolean {
    const mime = typeof contentType === 'string' ? contentType.trim().toLowerCase() : '';
    const isMedia = mime.startsWith('image/') || mime.startsWith('video/');
    return isMedia
        && expectedCloudMediaKindForEntityType(entityType) === null
        && !SYSTEM_MEDIA_ENTITY_TYPES.has(String(entityType || ''))
        && !isForbiddenRawCustomerMediaUpload(entityType, contentType);
}

function isCompleteDerivative(value: CloudMediaClassificationV1): boolean {
    return value.schemaVersion === 1
        && value.mediaClass === CLOUD_MEDIA_CLASSES.COMPRESSED_DERIVATIVE
        && typeof value.derivativeKind === 'string'
        && typeof value.transformProfile === 'string'
        && value.transformProfile.trim().length > 0
        && value.transformVersion === 1
        && typeof value.processor === 'string'
        && value.processor.trim().length > 0
        && typeof value.derivativeBytes === 'number'
        && Number.isFinite(value.derivativeBytes)
        && value.derivativeBytes > 0
        && typeof value.derivativeContentType === 'string'
        && value.derivativeContentType.trim().length > 0;
}

export function decideCloudMediaUpload(
    value: unknown,
    expectedKind: CloudMediaKind,
): CloudMediaUploadDecision {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return { allowed: false, reason: 'missing_derivative_attestation' };
    }
    const media = value as CloudMediaClassificationV1;
    if (media.mediaClass === CLOUD_MEDIA_CLASSES.LOCAL_ORIGINAL) {
        return { allowed: false, reason: 'raw_original_cloud_upload_forbidden' };
    }
    if (!isCompleteDerivative(media)) {
        return { allowed: false, reason: 'incomplete_derivative_attestation' };
    }
    if (media.derivativeKind !== expectedKind) {
        return { allowed: false, reason: 'derivative_kind_mismatch' };
    }
    return { allowed: true, reason: 'verified_compressed_derivative' };
}
